/**
 * Developers / API keys page (Sprint 288 Stream E; REQ-GW-005/010).
 *
 * Lists the tenant's gateway applications and virtual keys and allows
 * create/rotate/suspend/resume/revoke with Keto gating:
 * - ``view``    → read applications and keys (the guard already enforces it)
 * - ``operate`` → rotate, suspend, resume
 * - ``admin``   → create keys and applications, revoke
 *
 * The key secret is shown exactly once (creation/rotation) and is dropped from
 * state on close; internal ids are never rendered (ISS-634).
 */

import { useMemo, useState } from 'react';
import { Copy, KeyRound, Plus, RefreshCw, ShieldOff, ShieldCheck, Ban } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { AppModal } from '@/components/ui/app-modal';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { ConnectIdeCard } from '../components/ConnectIdeCard';
import { formatDateTime, KeyStatusChip } from '../components/KeyStatusChip';
import {
  useCreateApplication,
  useCreateKey,
  useGatewayApplications,
  useGatewayKeys,
  useRevokeKey,
  useRotateKey,
  useSetKeyStatus,
} from '../hooks/useGateway';
import type { GatewayKey } from '../types';

const AVAILABLE_SCOPES = [
  'models:invoke',
  'models:read',
  'embeddings:invoke',
  'responses:invoke',
  'messages:invoke',
  'agents:invoke',
  'usage:read',
] as const;

interface SecretReveal {
  name: string;
  secret: string;
}

export function GatewayDevelopersPage() {
  const { t } = useI18n();
  const canOperate = useModuleAccess('ai_gateway', 'operate');
  const canAdmin = useModuleAccess('ai_gateway', 'admin');

  const applicationsQuery = useGatewayApplications();
  const applications = useMemo(
    () => applicationsQuery.data?.data ?? [],
    [applicationsQuery.data],
  );
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const currentAppId = selectedAppId ?? applications[0]?.id ?? null;
  const currentApp = useMemo(
    () => applications.find((app) => app.id === currentAppId) ?? null,
    [applications, currentAppId],
  );

  const keysQuery = useGatewayKeys(currentAppId);
  const keys = keysQuery.data?.data ?? [];

  const createKey = useCreateKey();
  const rotateKey = useRotateKey();
  const revokeKey = useRevokeKey();
  const setStatus = useSetKeyStatus();
  const createApplication = useCreateApplication();

  const [secretReveal, setSecretReveal] = useState<SecretReveal | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(['models:invoke']);
  const [newAppName, setNewAppName] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState<GatewayKey | null>(null);

  const statusLabel = (status: string) =>
    (['active', 'suspended', 'revoked', 'expired'] as const).includes(
      status as 'active' | 'suspended' | 'revoked' | 'expired',
    )
      ? t(`gateway.status.${status}`)
      : status;

  function toggleScope(scope: string) {
    setNewKeyScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );
  }

  async function submitCreateKey() {
    if (!currentAppId || !newKeyName.trim() || newKeyScopes.length === 0) return;
    const created = await createKey.mutateAsync({
      application_id: currentAppId,
      name: newKeyName.trim(),
      scopes: newKeyScopes,
    });
    setCreateOpen(false);
    setNewKeyName('');
    setNewKeyScopes(['models:invoke']);
    setSecretReveal({ name: created.name, secret: created.secret });
  }

  async function submitCreateApplication() {
    // ISS-705: el primer tenant puede no tener aplicaciones. Si el nombre está
    // vacío se usa un nombre por defecto para no dejar la acción bloqueada.
    const name = newAppName.trim() || t('gateway.applications.defaultName');
    const created = await createApplication.mutateAsync({ name });
    setNewAppName('');
    setSelectedAppId(created.id);
  }

  async function submitRotate(key: GatewayKey) {
    const rotated = await rotateKey.mutateAsync(key);
    setSecretReveal({ name: rotated.name, secret: rotated.secret });
  }

  async function submitRevoke() {
    if (!confirmRevoke) return;
    await revokeKey.mutateAsync(confirmRevoke);
    setConfirmRevoke(null);
  }

  return (
    <div className="p-6 space-y-6" data-testid="gateway-developers-page">
      <PageHeader
        icon={KeyRound}
        title={t('gateway.title')}
        subtitle={t('gateway.subtitle')}
        testId="gateway-developers-header"
      />

      <ConnectIdeCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('gateway.applications.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {applicationsQuery.isLoading ? (
            <Spinner />
          ) : applicationsQuery.isError ? (
            <div
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
              role="alert"
              data-testid="applications-error"
            >
              <p>{t('gateway.applications.error')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => void applicationsQuery.refetch()}
                data-testid="applications-retry"
              >
                {t('gateway.applications.retry')}
              </Button>
            </div>
          ) : applications.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="no-apps">
              {t('gateway.applications.empty')}
            </p>
          ) : (
            <div className="flex items-end gap-4">
              <div className="w-72">
                <Label htmlFor="gateway-app-select">{t('gateway.applications.select')}</Label>
                <select
                  id="gateway-app-select"
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={currentAppId ?? ''}
                  onChange={(event) => setSelectedAppId(event.target.value)}
                >
                  {applications.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {canAdmin && (
            <div className="flex items-end gap-2">
              <div className="w-72">
                <Label htmlFor="gateway-new-app">{t('gateway.applications.new')}</Label>
                <Input
                  id="gateway-new-app"
                  value={newAppName}
                  onChange={(event) => setNewAppName(event.target.value)}
                  placeholder={t('gateway.applications.namePlaceholder')}
                />
              </div>
              <Button
                onClick={() => void submitCreateApplication()}
                disabled={createApplication.isPending}
                data-testid="create-application"
              >
                <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                {t('gateway.applications.create')}
              </Button>
            </div>
          )}
          {canAdmin && (
            <p className="text-xs text-muted-foreground" data-testid="application-default-hint">
              {t('gateway.applications.defaultHint')}
            </p>
          )}
          {createApplication.isError && (
            <p className="text-sm text-red-600" role="alert" data-testid="create-application-error">
              {t('gateway.applications.createError')}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('gateway.keys.title', { application: currentApp?.name ?? '' })}
          </CardTitle>
          {canAdmin && currentAppId && (
            <Button onClick={() => setCreateOpen(true)} data-testid="open-create-key">
              <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
              {t('gateway.keys.create')}
            </Button>
          )}
          {canAdmin && !currentAppId && (
            <span className="text-xs text-muted-foreground" data-testid="keys-create-hint">
              {t('gateway.keys.createAppFirst')}
            </span>
          )}
        </CardHeader>
        <CardContent>
          {!currentAppId ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('gateway.keys.needApplication')}
            </p>
          ) : keysQuery.isLoading ? (
            <Spinner />
          ) : keysQuery.isError ? (
            <div
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
              role="alert"
              data-testid="keys-error"
            >
              <p>{t('gateway.keys.error')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => void keysQuery.refetch()}
                data-testid="keys-retry"
              >
                {t('gateway.keys.retry')}
              </Button>
            </div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="no-keys">
              {t('gateway.keys.empty')}
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="keys-table">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2">{t('gateway.keys.columns.name')}</th>
                  <th className="py-2">{t('gateway.keys.columns.key')}</th>
                  <th className="py-2">{t('gateway.keys.columns.scopes')}</th>
                  <th className="py-2">{t('gateway.keys.columns.status')}</th>
                  <th className="py-2">{t('gateway.keys.columns.lastUsed')}</th>
                  <th className="py-2 text-right">{t('gateway.keys.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-2 font-medium">{key.name}</td>
                    <td className="py-2 font-mono text-xs">
                      {key.key_prefix}…{key.key_last4}
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-700"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2">
                      <KeyStatusChip status={key.status} label={statusLabel(key.status)} />
                    </td>
                    <td className="py-2">{formatDateTime(key.last_used_at)}</td>
                    <td className="py-2">
                      <div className="flex justify-end gap-2">
                        {canOperate && key.status === 'active' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void submitRotate(key)}
                              disabled={rotateKey.isPending}
                              data-testid={`rotate-${key.id}`}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" aria-hidden="true" />
                              {t('gateway.keys.actions.rotate')}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                void setStatus.mutateAsync({ key, action: 'suspend' })
                              }
                              data-testid={`suspend-${key.id}`}
                            >
                              <ShieldOff className="h-3 w-3 mr-1" aria-hidden="true" />
                              {t('gateway.keys.actions.suspend')}
                            </Button>
                          </>
                        )}
                        {canOperate && key.status === 'suspended' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void setStatus.mutateAsync({ key, action: 'resume' })}
                            data-testid={`resume-${key.id}`}
                          >
                            <ShieldCheck className="h-3 w-3 mr-1" aria-hidden="true" />
                            {t('gateway.keys.actions.resume')}
                          </Button>
                        )}
                        {canAdmin && key.status !== 'revoked' && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setConfirmRevoke(key)}
                            data-testid={`revoke-${key.id}`}
                          >
                            <Ban className="h-3 w-3 mr-1" aria-hidden="true" />
                            {t('gateway.keys.actions.revoke')}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <AppModal
          onClose={() => setCreateOpen(false)}
          ariaLabel={t('gateway.create.title')}
          testId="create-key-dialog"
        >
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>{t('gateway.create.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="new-key-name">{t('gateway.create.name')}</Label>
                <Input
                  id="new-key-name"
                  value={newKeyName}
                  onChange={(event) => setNewKeyName(event.target.value)}
                  placeholder={t('gateway.create.namePlaceholder')}
                />
              </div>
              <div>
                <Label>{t('gateway.create.scopes')}</Label>
                <div className="mt-2 space-y-1">
                  {AVAILABLE_SCOPES.map((scope) => (
                    <label key={scope} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newKeyScopes.includes(scope)}
                        onChange={() => toggleScope(scope)}
                        data-testid={`scope-${scope}`}
                      />
                      <span className="font-mono text-xs">{scope}</span>
                    </label>
                  ))}
                </div>
              </div>
              {createKey.isError && (
                <p className="text-sm text-red-600" data-testid="create-key-error">
                  {t('gateway.create.error')}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  {t('gateway.create.cancel')}
                </Button>
                <Button
                  onClick={() => void submitCreateKey()}
                  disabled={!newKeyName.trim() || newKeyScopes.length === 0 || createKey.isPending}
                  data-testid="submit-create-key"
                >
                  {t('gateway.create.submit')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}

      {secretReveal && (
        <AppModal
          onClose={() => setSecretReveal(null)}
          ariaLabel={t('gateway.secret.title', { name: secretReveal.name })}
          testId="secret-dialog"
        >
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>{t('gateway.secret.title', { name: secretReveal.name })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t('gateway.secret.warning')}
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 break-all rounded bg-gray-100 p-2 font-mono text-xs dark:bg-gray-800"
                  data-testid="secret-value"
                >
                  {secretReveal.secret}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void navigator.clipboard?.writeText(secretReveal.secret)}
                  data-testid="copy-secret"
                >
                  <Copy className="h-3 w-3 mr-1" aria-hidden="true" />
                  {t('gateway.secret.copy')}
                </Button>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setSecretReveal(null)} data-testid="close-secret">
                  {t('gateway.secret.close')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}

      {confirmRevoke && (
        <AppModal
          onClose={() => setConfirmRevoke(null)}
          ariaLabel={t('gateway.revoke.title', { name: confirmRevoke.name })}
          testId="revoke-dialog"
          dismissible={false}
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t('gateway.revoke.title', { name: confirmRevoke.name })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('gateway.revoke.warning')}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
                  {t('gateway.revoke.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void submitRevoke()}
                  disabled={revokeKey.isPending}
                  data-testid="confirm-revoke"
                >
                  {t('gateway.revoke.confirm')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}
    </div>
  );
}
